import { Dog } from ".";

// Common fields for both trail types
interface BaseTrail {
  id?: string;
  _id?: string;
  category: "mantrailing" | "hiking" | "detection" | "canicross";
  date: string | Date;
  distance?: number; // in meters
  duration?: number; // in seconds
  location?: string;
  notes?: string;
  createdAt?: string;
  userId?: string;
}

// Mantrailing specific fields (Trail schema)
export interface MantrailingTrail extends BaseTrail {
  category: "mantrailing";
  trainer?: string;
  dog: Dog;
  handlerName: string;
  trailType?: string;
  startType?: "knowing" | "blind" | string; // départ visuel ou à l'aveugle
  locationCoordinate?: [number, number]; // [lat, lon]
  runnerTrace?: any;
  dogTrace?: any;
  delay?: number; // in seconds
  trainerComment?: string;
  weather?: {
    temperature?: number;
    conditions?: string;
    windDirection?: string;
    windSpeed?: number;
    humidity?: number;
  };
}

export interface MantrailingTrailPayload extends Omit<MantrailingTrail, 'dog'> {
  dog: string; // Represents the dog's ID when sending data to the backend

  handlerName: string;
  trailType?: string;
  startType?: "knowing" | "blind" | string; // départ visuel ou à l'aveugle
  locationCoordinate?: [number, number]; // [lat, lon]
  runnerTrace?: any;
  dogTrace?: any;
  delay?: number; // in seconds
  trainerComment?: string;
}

// Hiking specific fields (Hike schema)
export interface HikingTrail extends BaseTrail {
  category: "hiking";
  name: string;
  description?: string;
  startLocation?: {
    type: "Point";
    coordinates: [number, number]; // [longitude, latitude]
  };
  difficulty?: "Easy" | "Moderate" | "Hard" | "Expert";
  elevationGain?: number | null; // in meters
  avgSpeed?: number;
  maxSpeed?: number;
  avgPace?: number;
  maxPace?: number;
  avgHeartRate?: number;
  maxHeartRate?: number;
  userTrack?: {
    type: string;
    features: any[];
    properties?: any; 
  } | null; // Allow null for userTrack
  dogTrack?: {
    type: string;
    features: any[];
  } | null; // Allow null for dogTrack
  userId?: string;
  photos?: string[];
  weather?: {
    temperature?: number;
    conditions?: string;
    windDirection?: string;
    windSpeed?: number;
    humidity?: number;
  };
}

// Detection specific fields
export interface DetectionTrail extends BaseTrail {
    category: "detection";
    dog: Dog;
    difficulty?: "Easy" | "Moderate" | "Hard" | "Expert";
    targetScent?: string;
    trainer?: string;
    trainerComment?: string;
}

export interface DetectionTrailPayload extends Omit<DetectionTrail, 'dog'> {
    dog: string; // dog ID
}

export interface CanicrossWeather {
  temperature?: number;
  conditions?: string;
  windDirection?: string;
  windSpeed?: number;
  humidity?: number;
}

export interface CanicrossTrail extends BaseTrail {
  category: "canicross";
  dogId: string | Dog;
  avgSpeed?: number;
  maxSpeed?: number;
  avgPace?: number;
  maxPace?: number;
  locationName?: string;
  avgHeartRate?: number;
  maxHeartRate?: number;
  positiveElevation?: number;
  negativeElevation?: number;
  terrain?: string;
  pauseTime?: number;
  energyLevel?: number;
  weather?: CanicrossWeather;
  locationPoint?: {
    type: "Point";
    coordinates: [number, number];
  };
  locationCoordinate?: [number, number];
  track?: any;
}

export interface CanicrossTrailPayload extends Omit<CanicrossTrail, "dogId" | "category" | "location"> {
  dogId: string;
  category?: "canicross";
  location?: {
    type: "Point";
    coordinates: [number, number];
  };
}

// Union type for both trail types
export type Trail = MantrailingTrail | HikingTrail | DetectionTrail | CanicrossTrail;

export type TrailCategory = "mantrailing" | "hiking" | "detection" | "canicross";

// Map data structure for displaying trails on map
export interface MapData {
  center: [number, number]; // [lat, lon]
  zoom: number;
  dogPath?: [number, number][]; // For mantrailing: dog path, for hiking: dog companion path
  victimPath?: [number, number][]; // For mantrailing: runner/victim path, for hiking: user path
}

// Helper type guards
export function isMantrailingTrail(trail: Trail): trail is MantrailingTrail {
  return trail.category === "mantrailing";
}

export function isHikingTrail(trail: Trail): trail is HikingTrail {
  return trail.category === "hiking";
}

export function isDetectionTrail(trail: Trail): trail is DetectionTrail {
    return trail.category === "detection";
}

export function isCanicrossTrail(trail: Trail): trail is CanicrossTrail {
  return trail.category === "canicross";
}

export interface ActivityTrackSample {
  coordinate?: [number, number];
  timestamp?: string;
  elevation?: number;
  cumulativeDistance?: number;
  speed?: number;
  pace?: number;
  heartRate?: number;
}

// GPX parsed data structure
export interface GpxData {
  path: [number, number][]; // [lat, lon]
  distance: number; // in meters
  center: [number, number]; // [lat, lon]
  elevationGain?: number; // in meters
  elevationLoss?: number; // in meters
  duration?: number; // in seconds
  date?: string;
  avgSpeed?: number; // in km/h
  maxSpeed?: number; // in km/h
  avgPace?: number; // in minutes per kilometer
  maxPace?: number; // fastest pace in minutes per kilometer
  avgHeartRate?: number; // in beats per minute
  maxHeartRate?: number; // in beats per minute
  startPoint?: [number, number]; // [lat, lon]
  endPoint?: [number, number]; // [lat, lon]
  timestamps?: Date[];
  samples?: ActivityTrackSample[];
}
